import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { IntegrationFailureListItem, IntegrationFailureDetail, IntegrationFailureListResponse, IntegrationFailureDetailResponse } from './integration.types';

// Standardized GET function for integration failures list
export async function fetchIntegrationFailures({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  jobDetailId,
  jobRunId,
  errorCode,
  stepId,
  nextRequestJson,
  fromDate,
  toDate,
  lastUpdateDateFrom,
  lastUpdateDateTo,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: number;
  jobDetailId?: number;
  jobRunId?: number;
  errorCode?: string;
  stepId?: string;
  nextRequestJson?: string;
  fromDate?: string;
  toDate?: string;
  lastUpdateDateFrom?: string;
  lastUpdateDateTo?: string;
  searched?: boolean;
}): Promise<IntegrationFailureListItem[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobDetailId) params.jobDetailId = jobDetailId;
  if (jobRunId) params.jobRunId = jobRunId;
  if (errorCode) params.errorCode = errorCode;
  if (stepId) params.stepId = stepId;
  if (nextRequestJson) params.nextRequestJson = nextRequestJson;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  if (lastUpdateDateFrom) params.lastUpdateDateFrom = lastUpdateDateFrom;
  if (lastUpdateDateTo) params.lastUpdateDateTo = lastUpdateDateTo;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.INTEGRATION.FAILURES);
  const response: IntegrationFailureListResponse = await api.get<IntegrationFailureListResponse>(endpoint, params);
  
  return response.map((row: any[]) => ({
    integrationId: row[0] || 0,
    stepId: row[1] || 0,
    configObjectJson: row[2] || null,
    errorResponse: row[3] || null,
    errorName: row[4] || '',
    date: row[5] || 0,
    nextRequestJson: row[6] || '',
    noOfRetries: row[7] || 0,
    jobId: row[8] || 0,
    errorCode: row[9] || '',
  }));
}

// GET integration failures count
export async function fetchIntegrationFailuresCount({
  clientCode,
  clientId,
  jobDetailId,
  jobRunId,
  errorCode,
  stepId,
  nextRequestJson,
  fromDate,
  toDate,
  lastUpdateDateFrom,
  lastUpdateDateTo,
  searched = true,
}: {
  clientCode: string;
  clientId?: number;
  jobDetailId?: number;
  jobRunId?: number;
  errorCode?: string;
  stepId?: string;
  nextRequestJson?: string;
  fromDate?: string;
  toDate?: string;
  lastUpdateDateFrom?: string;
  lastUpdateDateTo?: string;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobDetailId) params.jobDetailId = jobDetailId;
  if (jobRunId) params.jobRunId = jobRunId;
  if (errorCode) params.errorCode = errorCode;
  if (stepId) params.stepId = stepId;
  if (nextRequestJson) params.nextRequestJson = nextRequestJson;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  if (lastUpdateDateFrom) params.lastUpdateDateFrom = lastUpdateDateFrom;
  if (lastUpdateDateTo) params.lastUpdateDateTo = lastUpdateDateTo;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.INTEGRATION.FAILURE_DETAIL_COUNT);
  const response = await api.get<any>(endpoint, params);
  
  // Handle different response formats
  if (typeof response === 'number') {
    // API returns count directly as number
    return response;
  } else if (response && typeof response.count === 'number') {
    // API returns object with count property
    return response.count;
  }

  logger.warn('Invalid count response:', response);
  return 0;
}

// GET integration failure by ID
export async function fetchIntegrationFailureById({
  clientCode,
  failureId,
}: {
  clientCode: string;
  failureId: number;
}): Promise<IntegrationFailureDetail> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.INTEGRATION.FAILURES}/${failureId}`);
  const response: IntegrationFailureDetailResponse = await api.get<IntegrationFailureDetailResponse>(endpoint);
  
  if (response && response.length > 0) {
    const row = response[0];
    return {
      integrationId: row[0] || 0,
      stepId: row[1] || 0,
      configObjectJson: row[2] || null,
      errorResponse: row[3] || null,
      date: row[4] || 0,
      nextRequestJson: row[5] || '',
      noOfRetries: row[6] || 0,
      jobId: row[7] || 0,
      errorCode: row[8] || '',
    };
  }
  
  throw new Error('Integration failure not found');
}

// GET integration failure detail
export async function fetchIntegrationFailureDetail({
  clientCode,
  failureId,
}: {
  clientCode: string;
  failureId: number;
}): Promise<any> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.INTEGRATION.FAILURES}/${failureId}`);
  return api.get(endpoint);
}

// CREATE integration failure
export async function createIntegrationFailure({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<IntegrationFailureDetail> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.INTEGRATION.FAILURES);
  const response: IntegrationFailureDetailResponse = await api.post<IntegrationFailureDetailResponse>(endpoint, payload);
  
  if (response && response.length > 0) {
    const row = response[0];
    return {
      integrationId: row[0] || 0,
      stepId: row[1] || 0,
      configObjectJson: row[2] || null,
      errorResponse: row[3] || null,
      date: row[4] || 0,
      nextRequestJson: row[5] || '',
      noOfRetries: row[6] || 0,
      jobId: row[7] || 0,
      errorCode: row[8] || '',
    };
  }
  
  throw new Error('Failed to create integration failure');
}

// UPDATE integration failure
export async function updateIntegrationFailure({
  clientCode,
  failureId,
  payload,
}: {
  clientCode: string;
  failureId: number;
  payload: any;
}): Promise<IntegrationFailureDetail> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.INTEGRATION.FAILURES}/${failureId}`);
  const response: IntegrationFailureDetailResponse = await api.put<IntegrationFailureDetailResponse>(endpoint, payload);
  
  if (response && response.length > 0) {
    const row = response[0];
    return {
      integrationId: row[0] || 0,
      stepId: row[1] || 0,
      configObjectJson: row[2] || null,
      errorResponse: row[3] || null,
      date: row[4] || 0,
      nextRequestJson: row[5] || '',
      noOfRetries: row[6] || 0,
      jobId: row[7] || 0,
      errorCode: row[8] || '',
    };
  }
  
  throw new Error('Failed to update integration failure');
}

// DELETE integration failure
export async function deleteIntegrationFailure({
  clientCode,
  failureId,
}: {
  clientCode: string;
  failureId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.INTEGRATION.FAILURES}/${failureId}`);
  return api.delete(endpoint);
}

// GET error code count by job run ID
export async function fetchErrorCodeCount({
  clientCode,
  jobRunId,
}: {
  clientCode: string;
  jobRunId: number;
}): Promise<any[]> {
  const endpoint = getEndpoint(clientCode, `integrationInstanceFailureRest/getErrorCodeCount/${jobRunId}`);
  const response = await api.get<any>(endpoint);
  
  // Handle different response formats
  if (Array.isArray(response)) {
    return response;
  } else if (response && typeof response === 'object') {
    // If response is an object, try to extract array from data property
    if (Array.isArray((response as any).data)) {
      return (response as any).data;
    }
    // If it's a single object, wrap it in an array
    return [response];
  }
  
  return [];
}
